import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { API_BASE, apiFetch, apiUrl } from '../apiBase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // { id, username, points } | null
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch('/api/me');
      const json = await res.json();
      setUser(json.authed ? json : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live balance updates pushed from the bot/admin backend (points earned in
  // chat, admin adjustments, rain, game wins) without needing a poll.
  useEffect(() => {
    const socket = io(API_BASE || undefined, { path: '/socket.io' });
    socket.on('points:update', (payload) => {
      setUser((u) => (u && u.username?.toLowerCase() === payload.username?.toLowerCase()
        ? { ...u, points: payload.points }
        : u));
    });
    socket.on('points:tick', (payload) => {
      setUser((u) => {
        if (!u) return u;
        const mine = payload.rewarded?.find((r) => r.username?.toLowerCase() === u.username?.toLowerCase());
        return mine ? { ...u, points: mine.points } : u;
      });
    });
    return () => socket.disconnect();
  }, []);

  const login = useCallback((next = window.location.pathname) => {
    window.location.href = apiUrl(`/auth/kick/login?next=${encodeURIComponent(next)}`);
  }, []);

  const logout = useCallback(async () => {
    await apiFetch('/auth/logout', { method: 'POST' });
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
